import React from 'react';

import { FormattedMessage } from 'react-intl-next';
import { useSelector } from 'react-redux';
import { Link } from 'react-resource-router';

import Button from '@atlaskit/button/standard-button';
import BitbucketBranchesIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import Tooltip from '@atlaskit/tooltip';

import { Row } from 'src/components/details-card';
import { Expander } from 'src/components/sidebar';
import { useIntl } from 'src/hooks/intl';
import urls from 'src/sections/repository/urls';
import {
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
  getCurrentRepositoryMainBranchName,
} from 'src/selectors/repository-selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { AnnotatedRefData } from '../utils/use-fetch-annotated';

import messages from './commit-page.i18n';
import { BranchCardWrapper } from './commit-page.styled';

const BranchesIcon = (): JSX.Element => {
  const intl = useIntl();

  return (
    <BitbucketBranchesIcon label={intl.formatMessage(messages.branchesLabel)} />
  );
};

export const ExpandCommitBranches = ({
  annotatedRefs,
}: {
  annotatedRefs: AnnotatedRefData;
}): JSX.Element => {
  const owner = useSelector(getCurrentRepositoryOwnerName) || '';
  const repoSlug = useSelector(getCurrentRepositorySlug) || '';
  const mainBranch = useSelector(getCurrentRepositoryMainBranchName) || '';

  const mainBranchUrl = urls.ui.branch(owner, repoSlug, mainBranch);

  let message;
  if (annotatedRefs.branchNames.length < 2) message = messages.oneBranch;
  else message = messages.branches;
  const label = (
    <FormattedMessage
      {...message}
      values={{
        count: (
          <strong>
            {annotatedRefs.branchNames.length < 2
              ? 1
              : annotatedRefs.branchNames.length}
          </strong>
        ),
      }}
    />
  );

  const content =
    annotatedRefs.branchNames.length > 0 ? (
      annotatedRefs.branchNames.map(branchName => {
        const branchUrl = urls.ui.branch(owner, repoSlug, branchName);
        return (
          <Row key={branchName}>
            <Link
              href={branchUrl}
              onClick={() =>
                publishUiEvent({
                  action: 'clicked',
                  actionSubject: 'link',
                  actionSubjectId: 'branchLink',
                  source: 'commitPage',
                })
              }
            >
              {branchName}
            </Link>
          </Row>
        );
      })
    ) : (
      <Row key={mainBranch}>
        <Link
          href={mainBranchUrl}
          onClick={() =>
            publishUiEvent({
              action: 'clicked',
              actionSubject: 'link',
              actionSubjectId: 'branchLink',
              source: 'commitPage',
            })
          }
        >
          {mainBranch}
        </Link>
      </Row>
    );

  return (
    <>
      <Expander
        icon={<BranchesIcon />}
        label={label}
        isLoading={annotatedRefs.isLoading}
      >
        <BranchCardWrapper>{content}</BranchCardWrapper>
      </Expander>
    </>
  );
};

export const CollapseCommitBranches = ({
  annotatedRefs,
}: {
  annotatedRefs: AnnotatedRefData;
}): JSX.Element => {
  let message;
  const intl = useIntl();

  if (annotatedRefs.branchNames.length === 1) message = messages.oneBranch;
  else message = messages.branches;
  const label = annotatedRefs.isLoading ? (
    `${intl.formatMessage(messages.loadingBranches)}`
  ) : (
    <FormattedMessage
      {...message}
      values={{ count: annotatedRefs.branchNames.length }}
    />
  );
  return (
    <Tooltip content={label} position="left">
      <Button
        appearance="subtle"
        label={intl.formatMessage(messages.branchesLabel)}
        iconBefore={<BranchesIcon />}
      />
    </Tooltip>
  );
};
